'use client';

/**
 * OnboardingWizard — shows on first launch (or until dismissed).
 *
 * 4 steps:
 *   1. Your agency name + logo + accent color
 *   2. Add your first client workspace
 *   3. Connect a sender SMTP account (or skip)
 *   4. Run an Site Audit on the client's website (or skip)
 *
 * Persistence: localStorage 'gh_onboarding_done' set after finish.
 * Backend: also stamps agencyBranding.onboardingCompleted so a reset
 * could bring it back globally if desired.
 */

import { useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import { useAgencyBranding } from './AgencyBrandingProvider';

const STORAGE_KEY = 'gh_onboarding_done';

export default function OnboardingWizard() {
  const router = useRouter();
  const { branding, refresh } = useAgencyBranding();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState('');

  // Agency form
  const [agencyName, setAgencyName] = useState('');
  const [accentColor, setAccentColor] = useState('#2563eb');
  const [logoUrl, setLogoUrl] = useState('');

  // Client form
  const [clientName, setClientName] = useState('');
  const [clientWebsite, setClientWebsite] = useState('');

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const done = localStorage.getItem(STORAGE_KEY) === '1';
    // Also skip if user already changed the agency name from the default
    const alreadyBranded = branding?.agencyName && branding.agencyName !== 'Optilink AI Command Center';
    if (!done && !alreadyBranded) {
      // Defer so it doesn't fight the app's initial paint
      // FULL-SCAN §6: clear the timer when branding arrives, so the wizard doesn't pop on branded installs
      const t = setTimeout(() => setOpen(true), 1200);
      return () => clearTimeout(t);
    }
  }, [branding?.agencyName]);

  async function saveAgency() {
    if (!agencyName.trim()) { setStatus('Agency name is required'); return; }
    setBusy(true);
    setStatus('');
    try {
      await fetch('/api/agency-branding', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ agencyName: agencyName.trim(), accentColor, logoUrl: logoUrl.trim() }),
      });
      if (typeof window !== 'undefined') window.dispatchEvent(new Event('agency-branding-changed'));
      refresh();
      setStep(2);
    } catch (e) {
      setStatus('Failed: ' + e.message);
    } finally {
      setBusy(false);
    }
  }

  async function saveClient() {
    if (!clientName.trim()) { setStatus('Client name is required'); return; }
    setBusy(true);
    setStatus('');
    try {
      const res = await fetch('/api/workspaces', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: clientName.trim() }),
      });
      const data = await res.json();
      if (data?.success && data?.workspace?.id) {
        // Auto-switch to the new workspace
        await fetch('/api/workspaces/active', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: data.workspace.id }),
        });
        setStep(3);
      } else {
        setStatus('Failed: ' + (data?.error || 'unknown'));
      }
    } catch (e) {
      setStatus('Failed: ' + e.message);
    } finally {
      setBusy(false);
    }
  }

  function finish() {
    if (typeof window !== 'undefined') {
      localStorage.setItem(STORAGE_KEY, '1');
    }
    setOpen(false);
    router.refresh();
  }

  if (!open) return null;

  return (
    <div style={overlay}>
      <div style={card}>
        <div style={progressBar}>
          {[1, 2, 3, 4].map(n => (
            <div key={n} style={{
              flex: 1, height: 4,
              background: n <= step ? accentColor : 'rgba(15,23,42,0.08)',
              borderRadius: 2,
              transition: 'background 0.3s',
            }} />
          ))}
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 18 }}>
          <span style={{ fontSize: 11, color: '#475569', fontWeight: 700, letterSpacing: '0.08em' }}>
            STEP {step} OF 4
          </span>
          <button onClick={finish} style={{ background: 'transparent', border: 'none', color: '#475569', fontSize: 13, cursor: 'pointer' }}>
            Skip onboarding →
          </button>
        </div>

        {step === 1 && (
          <Pane title="👋 Welcome — let's brand your install" subtitle="Replace 'Optilink AI Command Center' with your agency's brand. You can change this anytime in Settings.">
            <Field label="Your agency name">
              <input
                autoFocus
                type="text"
                value={agencyName}
                onChange={e => setAgencyName(e.target.value)}
                placeholder="e.g. Acme Growth Studio"
                style={input}
              />
            </Field>
            <Field label="Accent color">
              <div style={{ display: 'flex', gap: 8 }}>
                <input
                  type="color"
                  value={accentColor}
                  onChange={e => setAccentColor(e.target.value)}
                  style={{ width: 44, height: 38, borderRadius: 8, border: '1px solid rgba(15,23,42,0.1)', background: 'transparent' }}
                />
                <input
                  type="text"
                  value={accentColor}
                  onChange={e => setAccentColor(e.target.value)}
                  style={{ ...input, flex: 1 }}
                />
              </div>
            </Field>
            <Field label="Logo URL (optional)">
              <input
                type="url"
                value={logoUrl}
                onChange={e => setLogoUrl(e.target.value)}
                placeholder="https://yourcdn.com/logo.png"
                style={input}
              />
            </Field>
            <Buttons primary={{ label: busy ? 'Saving...' : 'Continue →', onClick: saveAgency, disabled: busy }} />
          </Pane>
        )}

        {step === 2 && (
          <Pane title="🏢 Add your first client" subtitle="Each client lives in an isolated workspace. You can switch between them in one click from the sidebar.">
            <Field label="Client name">
              <input
                autoFocus
                type="text"
                value={clientName}
                onChange={e => setClientName(e.target.value)}
                placeholder="e.g. Acme Dental"
                style={input}
              />
            </Field>
            <Field label="Client website (optional — used for Site Audit in step 4)">
              <input
                type="url"
                value={clientWebsite}
                onChange={e => setClientWebsite(e.target.value)}
                placeholder="https://acmedental.com"
                style={input}
              />
            </Field>
            <Buttons
              primary={{ label: busy ? 'Creating...' : 'Continue →', onClick: saveClient, disabled: busy }}
              secondary={{ label: 'Back', onClick: () => setStep(1) }}
            />
          </Pane>
        )}

        {step === 3 && (
          <Pane title="📧 Connect a sender mailbox" subtitle="Your outreach goes out through your own SMTP — Gmail, Outlook, Hostinger, anything. You can do this later from the Sender Accounts page.">
            <p style={{ fontSize: 13, color: '#334155', margin: '6px 0 14px' }}>
              For now you can skip this and come back to it. Go to <strong>Sender Accounts</strong> in the sidebar when you're ready.
            </p>
            <Buttons
              primary={{ label: 'Open Sender Accounts →', onClick: () => { setOpen(false); router.push('/accounts'); } }}
              secondary={{ label: 'Skip for now', onClick: () => setStep(4) }}
            />
          </Pane>
        )}

        {step === 4 && (
          <Pane title="🔍 Run a Site Audit" subtitle="Point us at your client's website — we'll audit the site's speed, structure and on-page SEO so you can lead the call with facts.">
            {clientWebsite && (
              <p style={{ fontSize: 13, color: '#334155', margin: '0 0 14px' }}>
                We'll scan <strong style={{ color: accentColor }}>{clientWebsite}</strong>
              </p>
            )}
            <Buttons
              primary={{
                label: 'Open Instant Site Audit →',
                onClick: () => {
                  finish();
                  try { window.open('https://audit.optilinkai.com' + (clientWebsite ? '?url=' + encodeURIComponent(clientWebsite) : ''), '_blank', 'noopener'); } catch {}
                }
              }}
              secondary={{ label: 'Finish', onClick: finish }}
            />
          </Pane>
        )}

        {status && (
          <div style={{ marginTop: 12, padding: 10, borderRadius: 8, background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', fontSize: 12, color: '#dc2626' }}>
            {status}
          </div>
        )}
      </div>
    </div>
  );
}

const overlay = {
  position: 'fixed', inset: 0,
  background: 'rgba(0,0,0,0.7)',
  backdropFilter: 'blur(8px)',
  zIndex: 9999,
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  padding: 24,
};
const card = {
  width: '100%', maxWidth: 520,
  background: 'linear-gradient(135deg, #ffffff 0%, #f7f9fc 100%)',
  border: '1px solid rgba(37, 99, 235,0.25)',
  borderRadius: 18,
  padding: 28,
  boxShadow: '0 30px 80px rgba(0,0,0,0.6), 0 0 30px rgba(37, 99, 235,0.18)',
  color: '#0f172a',
};
const progressBar = { display: 'flex', gap: 6 };
const input = {
  width: '100%',
  background: '#ffffff',
  border: '1px solid rgba(15,23,42,0.1)',
  borderRadius: 10,
  padding: '12px 14px',
  color: '#0f172a',
  fontSize: 14,
  outline: 'none',
};
const accentColor = '#2563eb';

function Pane({ title, subtitle, children }) {
  return (
    <div style={{ marginTop: 18 }}>
      <h2 style={{ fontSize: 22, fontWeight: 800, margin: 0, letterSpacing: '-0.01em' }}>{title}</h2>
      <p style={{ fontSize: 13, color: '#475569', margin: '6px 0 18px' }}>{subtitle}</p>
      {children}
    </div>
  );
}
function Field({ label, children }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 14 }}>
      <span style={{ fontSize: 11, color: '#475569', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em' }}>{label}</span>
      {children}
    </label>
  );
}
function Buttons({ primary, secondary }) {
  return (
    <div style={{ display: 'flex', gap: 10, marginTop: 6 }}>
      <button
        onClick={primary.onClick}
        disabled={primary.disabled}
        style={{
          padding: '12px 22px',
          background: 'linear-gradient(90deg, #2563eb, #2563eb)',
          color: 'white', border: 'none', borderRadius: 10,
          fontSize: 14, fontWeight: 800, cursor: primary.disabled ? 'not-allowed' : 'pointer',
          flex: 1,
        }}
      >
        {primary.label}
      </button>
      {secondary && (
        <button
          onClick={secondary.onClick}
          style={{
            padding: '12px 18px',
            background: 'transparent', border: '1px solid rgba(15,23,42,0.1)',
            color: '#475569', borderRadius: 10, fontSize: 13, fontWeight: 600, cursor: 'pointer',
          }}
        >
          {secondary.label}
        </button>
      )}
    </div>
  );
}
